import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import type { PrepaidFieldChange, PrepaidHistoryEvent, PrepaidStatus } from '@dcc-erp/shared';
import { fmt, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PREPAID_ASSET_CODE, useDateMode } from './pp-helpers';
import { PPModifyModal } from './PPModifyModal';
import { PPSuspendModal } from './PPSuspendModal';
import { PPTerminateModal } from './PPTerminateModal';
import { PrepaidAttachmentsPanel } from './PrepaidAttachmentsPanel';
import {
  usePrepaidContract,
  usePrepaidHistory,
  usePrepaidSchedule,
  useResumePrepaidContract,
} from '../../lib/queries';

import type { PPItem } from './pp-helpers';

// ============================================================
// Contract detail side-drawer (P7 §3.4) — the host surface launched from a
// register row. Header + projected schedule (JV links) + lifecycle action bar
// (reuses the P6 modals) + a "ประวัติการแก้ไข" change-history tab (P7 T1).
// ============================================================

const HISTORY_EVENT_LABEL: Record<string, string> = {
  EDITED: 'แก้ไข',
  CANCELLED: 'ยกเลิกสัญญา',
  REVERSED: 'กลับรายการ',
  CREATED: 'สร้างสัญญา',
};
const HISTORY_FIELD_LABEL: Record<string, string> = {
  totalAmount: 'มูลค่าสัญญา',
  endDate: 'วันสิ้นสุด',
  status: 'สถานะ',
};

function contractStatusBadge(s: PrepaidStatus | undefined) {
  if (s === 'SUSPENDED') return <span className="badge warning">พักสัญญา</span>;
  if (s === 'TERMINATED') return <span className="badge neutral">ยกเลิกแล้ว</span>;
  if (s === 'FULLY_AMORTIZED') return <span className="badge positive">ตัดจ่ายครบ</span>;
  return <span className="badge brand">กำลังตัดจ่าย</span>;
}

function PPHistoryRow({ e }: { e: PrepaidHistoryEvent }) {
  const detail = (e.detail ?? {}) as { changes?: PrepaidFieldChange[] };
  const changes = detail.changes ?? [];
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 600 }}>{HISTORY_EVENT_LABEL[e.eventType] ?? e.eventType}</span>
        <span className="meta">
          {e.actorName}
          {e.createdAt ? ` · ${fmtDate(String(e.createdAt).slice(0, 10))}` : ''}
        </span>
      </div>
      {changes.map((c, i) => (
        <div key={i} className="meta" style={{ marginTop: 2 }}>
          {HISTORY_FIELD_LABEL[c.field] ?? c.field}: <b>{c.before ?? '—'}</b> →{' '}
          <b>{c.after ?? '—'}</b>
        </div>
      ))}
      {e.reason && (
        <div className="meta" style={{ marginTop: 2, fontStyle: 'italic' }}>
          เหตุผล: {e.reason}
        </div>
      )}
    </div>
  );
}

export function PPDetailDrawer({
  item,
  companyId,
  onClose,
  onClone,
}: {
  item: PPItem;
  companyId: string | undefined;
  onClose: () => void;
  onClone: (r: PPItem) => void;
}) {
  const { data: detail } = usePrepaidContract(companyId, item.apiId);
  const { data: schedule = [] } = usePrepaidSchedule(companyId, item.apiId);
  const { data: history = [] } = usePrepaidHistory(companyId, item.apiId);
  const resumeMut = useResumePrepaidContract(companyId);
  const { df } = useDateMode();
  const [lifecycle, setLifecycle] = useState<'terminate' | 'modify' | 'suspend' | null>(null);
  const [tab, setTab] = useState<'schedule' | 'history' | 'evidence'>('schedule');

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canAct = Boolean(item.apiId);
  const isActive = item.apiStatus === 'ACTIVE';
  const isSuspended = item.apiStatus === 'SUSPENDED';
  const isFrozen = item.apiStatus === 'TERMINATED' || item.apiStatus === 'FULLY_AMORTIZED';

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: 720,
          maxWidth: '96vw',
          background: '#fff',
          boxShadow: '-10px 0 30px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{item.id}</h3>
              {contractStatusBadge(item.apiStatus)}
            </div>
            <div className="meta" style={{ marginTop: 2 }}>
              {item.vendor} · {item.desc}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div className="meta">มูลค่าสัญญา</div>
              <div style={{ fontWeight: 600 }}>{fmt(item.total)}</div>
            </div>
            <div>
              <div className="meta">ตัดจ่าย/เดือน</div>
              <div style={{ fontWeight: 600 }}>{fmt(item.monthly)}</div>
            </div>
            <div>
              <div className="meta">คงเหลือ</div>
              <div style={{ fontWeight: 600 }}>{fmt(item.monthly * item.remain)}</div>
            </div>
            <div>
              <div className="meta">ช่วงตัดจ่าย</div>
              <div style={{ fontWeight: 600 }}>
                {df(item.start)} – {df(item.end)}
              </div>
            </div>
            {detail?.journalEntryId && (
              <div>
                <div className="meta">JV ตั้งต้น</div>
                <Link to="/journal" style={{ color: 'var(--brand)', fontWeight: 600 }}>
                  ดู JV
                </Link>
              </div>
            )}
          </div>
          {/* Lifecycle action bar — gated by the contract's real status. */}
          <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
            {canAct && isActive && (
              <button className="btn sm" onClick={() => setLifecycle('modify')}>
                ปรับปรุงตาราง
              </button>
            )}
            {canAct && isActive && (
              <button className="btn sm" onClick={() => setLifecycle('suspend')}>
                พักสัญญา
              </button>
            )}
            {canAct && isSuspended && (
              <button
                className="btn sm"
                disabled={resumeMut.isPending}
                onClick={() => item.apiId && resumeMut.mutate(item.apiId)}
              >
                {resumeMut.isPending ? 'กำลังเปิด…' : 'เปิดใช้ใหม่'}
              </button>
            )}
            {canAct && !isFrozen && (
              <button className="btn sm" onClick={() => setLifecycle('terminate')}>
                ยกเลิกสัญญา
              </button>
            )}
            {/* P9 — first-class clone / renew (prefills a new contract from this one). */}
            <button className="btn sm" onClick={() => onClone(item)}>
              คัดลอก / ต่ออายุ
            </button>
            <Link
              to="/ledger"
              search={{ accountCode: PREPAID_ASSET_CODE }}
              className="btn sm ghost"
            >
              ดูใน GL
            </Link>
          </div>
        </div>

        <div className="filter-row" style={{ padding: '8px 20px 0' }}>
          <button
            className={`tab ${tab === 'schedule' ? 'active' : ''}`}
            onClick={() => setTab('schedule')}
          >
            ตารางตัดจ่าย
          </button>
          <button
            className={`tab ${tab === 'history' ? 'active' : ''}`}
            onClick={() => setTab('history')}
          >
            ประวัติการแก้ไข {history.length > 0 ? `(${history.length})` : ''}
          </button>
          {canAct && (
            <button
              className={`tab ${tab === 'evidence' ? 'active' : ''}`}
              onClick={() => setTab('evidence')}
            >
              เอกสารหลักฐาน{' '}
              {(item.missingEvidenceCount ?? 0) > 0 ? (
                <span style={{ color: 'var(--warning,#b4780a)' }}>⚠</span>
              ) : null}
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px' }}>
          {tab === 'evidence' && item.apiId ? (
            <PrepaidAttachmentsPanel
              companyId={companyId}
              contractId={item.apiId}
              requiresEvidence={item.requiresEvidence ?? false}
            />
          ) : tab === 'schedule' ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>งวด</th>
                  <th>เดือน</th>
                  <th className="num">จำนวน</th>
                  <th className="num">คงเหลือ</th>
                  <th>สถานะ</th>
                  <th>JV</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((r) => (
                  <tr key={r.index}>
                    <td>{r.index}</td>
                    <td>{fmtDate(String(r.voucherDate).slice(0, 10))}</td>
                    <td className="num tnum">{fmt(Number(r.amount))}</td>
                    <td className="num tnum">{fmt(Number(r.closingBalance))}</td>
                    <td>
                      {r.status === 'posted' ? (
                        <span className="badge positive sm">ลงแล้ว</span>
                      ) : r.status === 'current' ? (
                        <span className="badge brand sm">งวดนี้</span>
                      ) : (
                        <span className="badge sm">รอ</span>
                      )}
                    </td>
                    <td>
                      {r.journalEntryId ? (
                        <Link to="/journal" style={{ color: 'var(--brand)' }}>
                          ดู
                        </Link>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : history.length === 0 ? (
            <div className="meta" style={{ textAlign: 'center', padding: '24px 0' }}>
              ยังไม่มีประวัติการแก้ไข
            </div>
          ) : (
            history.map((e) => <PPHistoryRow key={e.id} e={e} />)
          )}
        </div>
      </div>

      {lifecycle === 'terminate' && (
        <PPTerminateModal item={item} companyId={companyId} onClose={() => setLifecycle(null)} />
      )}
      {lifecycle === 'modify' && (
        <PPModifyModal item={item} companyId={companyId} onClose={() => setLifecycle(null)} />
      )}
      {lifecycle === 'suspend' && (
        <PPSuspendModal item={item} companyId={companyId} onClose={() => setLifecycle(null)} />
      )}
    </div>
  );
}
